import Phaser from 'phaser';
import GameService from '../services/GameService';
import { itemManager } from './ItemManager';

const gameService = new GameService(process.env.REACT_APP_API_URL || '');

class InventoryManager {
    constructor(scene = null) {
        this.scene = scene;
        this.inventory = new Map();
        this.lastSync = null;
        this.pendingChanges = new Map();
        // OPTIMIZED: Reasonable sync intervals with debouncing
        this.SYNC_INTERVAL = 5 * 60 * 1000; // Keep 5 minutes - this is already reasonable
        this.DEBOUNCE_DELAY = 3000; // 3 second debounce for inventory changes
        this.userId = null;
        this.walletAddress = null;
        this.syncing = false;
        this.syncDebounceTimer = null;
        this.events = new Phaser.Events.EventEmitter();
        
        this.GRID_COLS = 7;
        this.GRID_ROWS = 5;
    }

    setScene(scene) {
        this.scene = scene;
    }

    getGameService() {
        return window.gameService;
    }

    async initialize(userId, walletAddress) {
        this.userId = userId;
        this.walletAddress = walletAddress;
        
        try {
            const gameService = this.getGameService();
            if (!gameService) {
                throw new Error('GameService not initialized');
            }

            // Ensure wallet address is set
            gameService.setWalletAddress(this.walletAddress);
    
            // Get inventory data using userId
            const dbInventory = await gameService.getInventory(userId);
            
            // Clear current inventory
            this.inventory.clear();
            
            // Convert database records to inventory format
            dbInventory.forEach(item => {
                let itemCode;
                
                // Handle new format with item_category_code
                if (item.item_category_code !== undefined && item.item_category_code !== null) {
                    const category = itemManager.getCategoryFromCode(item.item_category_code);
                    if (category) {
                        itemCode = `${category}_${item.item_code}`;
                    } else {
                        console.warn('Unknown category code:', item.item_category_code);
                        itemCode = `Consumable_${item.item_code}`; // Fallback
                    }
                } else {
                    // Handle old format (fallback to Consumable for now)
                    itemCode = `${item.item_type || 'Consumable'}_${item.item_code}`;
                }

                this.inventory.set(itemCode, {
                    quantity: item.quantity,
                    gridX: item.slot_x !== null ? item.slot_x : null,
                    gridY: item.slot_y !== null ? item.slot_y : null,
                    categoryCode: item.item_category_code,
                    rawItemCode: item.item_code  // Renamed to avoid conflict
                });
            });
            
            this.lastSync = Date.now();
            return true;
        } catch (error) {
            console.error('Error initializing inventory:', error);
            return false;
        }
    }

    // OPTIMIZED: Debounced sync to prevent excessive database calls
    debouncedSync() {
        // Clear existing timer
        if (this.syncDebounceTimer) {
            clearTimeout(this.syncDebounceTimer);
        }
        
        // Set new timer
        this.syncDebounceTimer = setTimeout(() => {
            this.syncWithDatabase();
        }, this.DEBOUNCE_DELAY);
    }

    async syncWithDatabase() {
        if (!this.userId || this.syncing) return false;
        
        const gameService = this.getGameService();
        if (!gameService) {
            console.error('GameService not initialized');
            return false;
        }

        // Ensure wallet address is set
        gameService.setWalletAddress(this.walletAddress);

        this.syncing = true;
        try {
            // Apply pending changes first
            for (const [itemCode, changes] of this.pendingChanges.entries()) {
                const itemId = parseInt(itemCode.split('_')[1]);
                if (changes.quantity !== 0 || changes.gridX !== undefined) {
                    // Extract category from itemCode (e.g., "Consumable_6" -> "Consumable")
                    const category = itemCode.split('_')[0];
                    // Find categoryCode from metadata
                    let categoryCode = 0; // Default to Consumable
                    if (itemManager.metadata && itemManager.metadata.categoryIndex) {
                        const categoryEntry = Object.entries(itemManager.metadata.categoryIndex)
                            .find(([, name]) => name === category);
                        if (categoryEntry) {
                            categoryCode = parseInt(categoryEntry[0]);
                        }
                    }
                    
                    await gameService.updateInventory(
                        itemId,
                        changes.quantity || 0,
                        changes.gridX,
                        changes.gridY,
                        categoryCode
                    );
                }
            }

            // Get fresh inventory
            const dbInventory = await gameService.getInventory(this.userId);
            
            // Update local cache
            this.inventory.clear();
            dbInventory.forEach(item => {
                // Skip items with 0 or negative quantity
                if (item.quantity <= 0) {
                    return;
                }
                
                let itemCode;
                
                // Handle new format with item_category_code
                if (item.item_category_code !== undefined && item.item_category_code !== null) {
                    const category = itemManager.getCategoryFromCode(item.item_category_code);
                    if (category) {
                        itemCode = `${category}_${item.item_code}`;
                    } else {
                        console.warn('Unknown category code:', item.item_category_code);
                        itemCode = `Consumable_${item.item_code}`; // Fallback
                    }
                } else {
                    // Handle old format (fallback to Consumable for now)
                    itemCode = `${item.item_type || 'Consumable'}_${item.item_code}`;
                }

                this.inventory.set(itemCode, {
                    quantity: item.quantity,
                    gridX: item.slot_x,
                    gridY: item.slot_y,
                    categoryCode: item.item_category_code,
                    rawItemCode: item.item_code  // Renamed to avoid conflict
                });
            });

            this.lastSync = Date.now();
            this.pendingChanges.clear();
            
            this.events.emit('inventory-updated');
            return true;
        } catch (error) {
            console.error('Error syncing inventory:', error);
            return false;
        } finally {
            this.syncing = false;
        }
    }

    shouldSync() {
        return !this.lastSync || (Date.now() - this.lastSync > this.SYNC_INTERVAL);
    }

    findEmptyGridPosition() {
        const occupiedPositions = new Set();
        
        // Mark all occupied positions
        this.inventory.forEach(item => {
            if (item.gridX !== null && item.gridY !== null) {
                occupiedPositions.add(`${item.gridX},${item.gridY}`);
            }
        });

        // Find first empty position
        for (let row = 0; row < this.GRID_ROWS; row++) {
            for (let col = 0; col < this.GRID_COLS; col++) {
                if (!occupiedPositions.has(`${col},${row}`)) {
                    return { gridX: col, gridY: row };
                }
            }
        }

        return null;
    }

    async addItem({ itemCode, categoryCode = null, quantity = 1, gridX = null, gridY = null }) {
        try {
            const gameService = this.getGameService();
            if (!gameService) {
                throw new Error('GameService not initialized');
            }

            // Ensure wallet address is set
            gameService.setWalletAddress(this.walletAddress);

            if (!this.scene) {
                throw new Error('Scene not set in InventoryManager');
            }
    
            const gameStore = this.scene.game.registry.get('gameStore');
            if (!gameStore || !gameStore.userId) {
                throw new Error('No user ID found in game registry');
            }
    
            let itemId, actualCategoryCode;
            
            // Handle new format with categoryCode provided
            if (categoryCode !== null) {
                itemId = parseInt(itemCode);
                actualCategoryCode = categoryCode;
            } else {
                // Handle old format (itemCode like "Consumable_6")
                const parts = itemCode.split('_');
                if (parts.length >= 2) {
                    itemId = parseInt(parts[1]);
                    // For now, assume Consumable category (categoryCode 0)
                    actualCategoryCode = 0;
                } else {
                    throw new Error('Invalid item code format');
                }
            }
            
            if (isNaN(itemId)) {
                throw new Error('Invalid item ID');
            }
    
            if (gridX === null || gridY === null) {
                const emptyPos = this.findEmptyGridPosition();
                if (emptyPos) {
                    gridX = emptyPos.gridX;
                    gridY = emptyPos.gridY;
                } else {
                    throw new Error('No empty inventory slots available');
                }
            }
    
            // Create proper itemCode for storage
            const category = itemManager.getCategoryFromCode(actualCategoryCode);
            const fullItemCode = category ? `${category}_${itemId}` : itemCode;
            
            const existing = this.inventory.get(fullItemCode);
            
            // If item already exists, just add to the stack without moving it
            if (existing) {
                // For existing items, only update quantity in backend, don't change position
                const dbResult = await gameService.updateInventory(
                    itemId,
                    quantity,
                    null, // Don't update position for existing items
                    null, // Don't update position for existing items
                    actualCategoryCode
                );
                
                if (dbResult) {
                    existing.quantity += quantity;
                    // Don't update grid position for existing items
                    this.events.emit('inventory-updated');
                    return true;
                }
            } else {
                // For new items, use the provided or found grid position
                const dbResult = await gameService.updateInventory(
                    itemId,
                    quantity,
                    gridX,
                    gridY,
                    actualCategoryCode
                );
                
                if (dbResult) {
                    this.inventory.set(fullItemCode, { 
                        quantity, 
                        gridX, 
                        gridY,
                        categoryCode: actualCategoryCode,
                        rawItemCode: itemId  // Renamed to avoid conflict
                    });
                    
                    this.events.emit('inventory-updated');
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('Error adding item to inventory:', error);
            throw error;
        }
    }

    removeItem(itemCode, quantity = 1) {
        const existing = this.inventory.get(itemCode);
        if (!existing) return false;

        // Update local inventory
        if (existing.quantity <= quantity) {
            this.inventory.delete(itemCode);
        } else {
            existing.quantity -= quantity;
        }

        // Track change for database sync
        const pendingChange = this.pendingChanges.get(itemCode) || { 
            quantity: 0, 
            gridX: existing.gridX, 
            gridY: existing.gridY 
        };
        pendingChange.quantity = (pendingChange.quantity || 0) - quantity;
        this.pendingChanges.set(itemCode, pendingChange);
        
        // Notify listeners
        this.events.emit('inventory-updated');
        return true;
    }

    updateItemPosition(itemCode, gridX, gridY) {
        // Validate grid position
        if (gridX < 0 || gridX >= this.GRID_COLS || gridY < 0 || gridY >= this.GRID_ROWS) {
            console.error('Invalid grid position:', { gridX, gridY });
            return false;
        }

        const item = this.inventory.get(itemCode);
        if (!item) return false;

        // Update local inventory
        item.gridX = gridX;
        item.gridY = gridY;

        // Track change for database sync
        const pendingChange = this.pendingChanges.get(itemCode) || { quantity: 0 };
        pendingChange.gridX = gridX;
        pendingChange.gridY = gridY;
        this.pendingChanges.set(itemCode, pendingChange);

        // Get item ID and categoryCode from itemCode
        const itemId = parseInt(itemCode.split('_')[1]);
        if (!isNaN(itemId) && this.userId) {
            // Extract category from itemCode and find categoryCode
            const category = itemCode.split('_')[0];
            let categoryCode = 0; // Default to Consumable
            if (itemManager.metadata && itemManager.metadata.categoryIndex) {
                const categoryEntry = Object.entries(itemManager.metadata.categoryIndex)
                    .find(([, name]) => name === category);
                if (categoryEntry) {
                    categoryCode = parseInt(categoryEntry[0]);
                }
            }
            
            // Immediately update position in database
            const gameService = this.getGameService();
            if (gameService) {
                gameService.setWalletAddress(this.walletAddress);
                gameService.updateItemPosition(this.userId, itemId, gridX, gridY, categoryCode)
                    .catch(error => {
                        console.error('Error updating item position in database:', error);
                    });
            }
        }
        
        return true;
    }

    getInventory() {
        return Array.from(this.inventory.entries()).map(([itemCode, data]) => ({
            itemCode,
            ...data
        }));
    }

    async forceSync() {
        return this.syncWithDatabase();
    }

    hasItem(itemCode) {
        return this.inventory.has(itemCode);
    }

    getItemQuantity(itemCode) {
        const item = this.inventory.get(itemCode);
        return item ? item.quantity : 0;
    }

    getItemPosition(itemCode) {
        const item = this.inventory.get(itemCode);
        return item ? { gridX: item.gridX, gridY: item.gridY } : null;
    }

    hasInventorySpace() {
        // Check if inventory has space (max 35 slots = 7 cols × 5 rows)
        const maxSlots = this.GRID_COLS * this.GRID_ROWS;
        const currentItems = this.inventory.size;
        return currentItems < maxSlots;
    }

    destroy() {
        this.events.removeAllListeners();
        this.inventory.clear();
        this.pendingChanges.clear();
        this.scene = null;
        this.walletAddress = null;
    }
}

// Export singleton instance
const inventoryManagerInstance = new InventoryManager();

export const initInventoryManager = (scene) => {
    inventoryManagerInstance.setScene(scene);
    return inventoryManagerInstance;
};

export const getInventoryManager = () => {
    return inventoryManagerInstance;
};

export { inventoryManagerInstance as inventoryManager };